import { Autocomplete, Button, Divider, Drawer, FormLabel, Grid, IconButton, InputAdornment, Paper, TextField, Typography } from "@mui/material"
import { FC, useEffect, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import { Formik } from "formik"
import { Supplier } from "../../../../types/slices/supplierType"
import { useSupplierStore } from "../../../../hooks/useSupplierStore"
import { ApiStatus } from "../../../../types/api/status"
import { AccountReceivableListDrawer } from "./AccountReceivableListDrawer"

interface PaymentVoucherFormDrawerProps {
    open: boolean
    onClose: () => void
    onList: () => Promise<void>
}

export interface PaymentVoucherFormData {
    idsupplier: number
    idway_to_pay: number
    idsource_bank?: number
    idreceiving_bank?: number
    series?: string
    document_date?: string
    amount: number | undefined
}

interface FormErrors {
    idsupplier?: string
    idway_to_pay?: string
    idsource_bank?: string
    idreceiving_bank?: string
    series?: string
    document_date?: string
    amount?: string
}

const wayToPays = [
    { id: 39, name: "Efectivo" },
    { id: 18, name: "Cheque" },
    { id: 24, name: "Transferencia" }
]

const newDate = new Date()
const today = newDate.toISOString().split('T')[0]

export const PaymentVoucherFormDrawer: FC<PaymentVoucherFormDrawerProps> = (props) => {
    const { open, onClose, onList } = props

    const { status: statusSupplier, supppliers, getSuppliers } = useSupplierStore()

    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
    const [selectedWayToPay, setSelectedWayToPay] = useState<any>(wayToPays[0])

    const [values, setValues] = useState<PaymentVoucherFormData>({} as PaymentVoucherFormData)
    const [showDrawer, setShowDrawer] = useState<boolean>(false)

    const [cartolaConfig, setCartolaConfig] = useState<string>("2");

    const data: PaymentVoucherFormData = {
        idsupplier: 0,
        idway_to_pay: wayToPays[0]?.id || 0,
        series: '',
        document_date: undefined,
        amount: undefined
    }

    useEffect(() => {
        getSuppliers()
    }, [])

    useEffect(() => {
        if (selectedSupplier) {
            setCartolaConfig(selectedSupplier.cartola_config)
        }
    }, [selectedSupplier]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'F2') {
                event.preventDefault()
                const form = document.getElementById("form-container") as HTMLFormElement
                if (form?.checkValidity()) {
                    document.getElementById("submit-button")?.click()
                }
            } else if (event.key === 'F3') {
                event.preventDefault()
                document.getElementById("idsupplier")?.focus()
            } else if (event.key === 'Escape') {
                event.preventDefault()
                onClose()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [onClose])

    const validateForm = (values: PaymentVoucherFormData) => {
        let errors: FormErrors = {}

        if (!values.idsupplier) errors.idsupplier = "el campo es requerido"
        if (!values.idway_to_pay) errors.idway_to_pay = "el campo es requerido"
        if (!values.amount) errors.amount = "el campo es requerido"

        if ((values?.idway_to_pay === 18 || values?.idway_to_pay === 24) && !values.document_date) errors.document_date = "el campo es requerido"
        if ((values?.idway_to_pay === 18) && !values.series) errors.series = "el campo es requerido"

        return errors
    }

    const onSubmit = async (values: PaymentVoucherFormData) => {
        let finalAmount = Number(values.amount) || 0;
        if (cartolaConfig === "2" && Number(selectedSupplier?.balance_infavor) > 0) {
            finalAmount = Math.max(0, finalAmount + Number(selectedSupplier?.balance_infavor));
        }
    
        setValues({ ...values, amount: finalAmount });
        setShowDrawer(true);
    };
    
    

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Grid container sx={{ width: 500, padding: 2 }} spacing={2}>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8
                    }}
                    tabIndex={-1}
                >
                    <CloseIcon />
                </IconButton>

                <Grid item xs={12}>
                    <Typography variant="h6">
                        Registrar nuevo pago
                    </Typography>
                </Grid>
                <Grid item xs={12} mb={2}>
                    <Divider />
                </Grid>

                <Formik
                    initialValues={data}
                    enableReinitialize
                    validate={(values) => validateForm(values)}
                    onSubmit={onSubmit}
                >
                    {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isValid }) => (
                        <form id="form-container">
                            <Grid container sx={{ width: 500, padding: 2 }} spacing={2}>
                                <Grid item xs={12}>
                                    <FormLabel>Cliente/Proveedor:</FormLabel>
                                    <Autocomplete
                                        id="idsupplier"
                                        size="small"
                                        value={selectedSupplier || null}
                                        options={supppliers || []}
                                        getOptionLabel={(option) => option.name_rz + ' - ' + option.rut}
                                        isOptionEqualToValue={(option, value) => option.name_rz === value?.name_rz}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                error={errors.idsupplier && touched.idsupplier ? true : false}
                                                helperText={errors.idsupplier && touched.idsupplier ? errors.idsupplier : ''}
                                                autoFocus
                                            />
                                        )}
                                        onChange={(e, newValue: any) => {
                                            setSelectedSupplier(newValue)
                                            setFieldValue('idsupplier', newValue?.id || null)
                                        }}
                                        loading={statusSupplier === ApiStatus.FETCHING}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormLabel>Método de pago:</FormLabel>
                                    <Autocomplete
                                        id="idway_to_pay"
                                        size="small"
                                        value={selectedWayToPay || null}
                                        options={wayToPays || []}
                                        getOptionLabel={(option) => option?.name || ''}
                                        isOptionEqualToValue={(option, value) => option.name === value?.name}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                error={errors.idway_to_pay && touched.idway_to_pay ? true : false}
                                                helperText={errors.idway_to_pay && touched.idway_to_pay ? errors.idway_to_pay : ''}
                                            />
                                        )}
                                        onChange={(e, newValue: any) => {
                                            setSelectedWayToPay(newValue)
                                            setFieldValue('idway_to_pay', newValue?.id || null)
                                            if (newValue?.id === 18) setFieldValue('document_date', today)
                                            if (newValue?.id === 24) setFieldValue('document_date', today)
                                        }}
                                    />
                                </Grid>

                                {(values?.idway_to_pay === 18) && (
                                    <Grid item xs={12}>
                                        <FormLabel>Serie:</FormLabel>
                                        <TextField
                                            id="series"
                                            size="small"
                                            fullWidth
                                            value={values.series}
                                            onChange={handleChange}
                                            error={errors.series && touched.series ? true : false}
                                            helperText={errors.series && touched.series ? errors.series : ''}
                                        />
                                    </Grid>
                                )}

                                {(values?.idway_to_pay === 18 || values?.idway_to_pay === 24) && (
                                    <Grid item xs={12}>
                                        <FormLabel>Fecha documento</FormLabel>
                                        <TextField
                                            id="document_date"
                                            size="small"
                                            type="date"
                                            fullWidth
                                            value={values.document_date}
                                            onChange={handleChange}
                                            onKeyDown={(e) => {
                                                if (e.key === "Tab") {
                                                    e.preventDefault()
                                                    const nextInput = document.getElementById("amount")
                                                    nextInput?.focus()
                                                }
                                            }}
                                            error={errors.document_date && touched.document_date ? true : false}
                                            helperText={errors.document_date && touched.document_date ? errors.document_date : ''}
                                            inputProps={{
                                                min: today
                                            }}
                                        />

                                    </Grid>
                                )}

                                {cartolaConfig === "2" && (
                                    <Grid item xs={12}>
                                        <FormLabel>Saldo a favor:</FormLabel>
                                        <TextField
                                            id="balance"
                                            size="small"
                                            fullWidth
                                            value={selectedSupplier?.balance_infavor || 0}
                                            disabled
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <span>$</span>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )}



                                <Grid item xs={12}>
                                    <FormLabel>Monto:</FormLabel>
                                    <TextField
                                        id="amount"
                                        type="text"
                                        fullWidth
                                        size="small"
                                        value={values.amount}
                                        onChange={(e) => {
                                            const { value } = e.target
                                            const isValid = /^\d+$/.test(value)
                                            if (isValid || value === "") {
                                                setFieldValue('amount', value)
                                            }
                                        }}
                                        error={errors.amount && touched.amount ? true : false}
                                        helperText={errors.amount && touched.amount ? errors.amount : ''}
                                        inputProps={{
                                            min: 1,
                                            inputMode: "numeric",
                                            pattern: "[0-9]*"
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span>$</span>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        id="submit-button"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => isValid && handleSubmit()}
                                        fullWidth
                                        sx={{
                                            fontFamily: "AvenirNextRounded",
                                            fontWeight: "Demi",
                                            fontSize: "13px",
                                            backgroundColor: "#516E80 !important",
                                            border: "solid 1px #516E80 !important",
                                            color: "#FFFFFF !important",
                                            cursor: "pointer",
                                            transition: ".5s",
                                            textTransform: "none !important",
                                            minWidth: "100px !important",
                                            padding: "6px 12px",
                                            borderRadius: "20px",
                                            "&:hover": {
                                                backgroundColor: "#3F5B6F !important",
                                                borderColor: "#3F5B6F !important",
                                            },
                                        }}
                                        tabIndex={-1}
                                    >
                                        {"Confirmar"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </Grid>

            <Grid item xs={12} mb={2}>
                <Divider />
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={0} sx={{ padding: 2, backgroundColor: "#f5f5f5" }}>
                    <Typography variant="subtitle1"><strong>Atajos de Teclado:</strong></Typography>
                    <Grid container direction="column" spacing={1}>
                        <Grid item>
                            <Typography variant="body2">✅ <strong>F2:</strong> Confirmar</Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2">🔍 <strong>F3:</strong> Buscar cliente/proveedor</Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2">❌ <strong>Esc:</strong> Cerrar formulario</Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2">⇥ <strong>Tab:</strong> Navegar entre campos</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {showDrawer && (
                <AccountReceivableListDrawer
                    open={showDrawer}
                    onClose={() => setShowDrawer(false)}
                    PaymentVoucher={values}
                    onList={onList}
                    onClose2={onClose}
                />
            )}
        </Drawer>
    )
}